import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { StatCard } from '../components/shared/StatCard';
import { Toggle }   from '../components/shared/Toggle';
import { Modal }    from '../components/shared/Modal';
import { Spinner }  from '../components/shared/Spinner';
import { menuAPI, categoriesAPI } from '../services/api';
import { fmt, mgn, mgnClass }    from '../components/shared/fmt';
import { useTitle } from '../hooks/useTitle';

export const MenuPage: React.FC = () => {
  useTitle('Menu Items');
  const qc = useQueryClient();
  const { data: md } = useQuery({ queryKey:['menu'], queryFn: () => menuAPI.getAll() });
  const { data: cd } = useQuery({ queryKey:['cats'], queryFn: () => categoriesAPI.getAll() });
  const menu = md?.data || [];
  const cats = cd?.data || [];
  const [fCat,   setFCat]   = useState('all');
  const [search, setSearch] = useState('');
  const [modal,  setModal]  = useState(false);
  const [sel,    setSel]    = useState<any>(null);
  const [form,   setForm]   = useState({ name:'', catId:'', price:'', cost:'', emoji:'🍽️', description:'' });

  const saveMut   = useMutation({ mutationFn:(d:any)=>sel?menuAPI.update(sel.id,d):menuAPI.create(d), onSuccess:()=>{ toast.success(sel?'Updated':'Added'); qc.invalidateQueries({queryKey:['menu']}); setModal(false); } });
  const toggleMut = useMutation({ mutationFn: menuAPI.toggle, onSuccess:()=>qc.invalidateQueries({queryKey:['menu']}) });
  const delMut    = useMutation({ mutationFn: menuAPI.delete, onSuccess:()=>{ toast.success('Deleted'); qc.invalidateQueries({queryKey:['menu']}); } });

  const visible = menu.filter((i:any) => (fCat==='all'||i.catId===fCat) && (!search||i.name.toLowerCase().includes(search.toLowerCase())));

  const openAdd  = () => { setSel(null); setForm({name:'',catId:cats[0]?.id||'',price:'',cost:'',emoji:'🍽️',description:''}); setModal(true); };
  const openEdit = (i:any) => { setSel(i); setForm({name:i.name,catId:i.catId,price:String(i.price),cost:String(i.cost),emoji:i.emoji,description:i.description||''}); setModal(true); };

  return (
    <div>
      <div className="page-title">Menu Management</div>
      <div className="page-sub">{menu.length} items</div>

      <div className="stat-grid">
        <StatCard label="Total Items"  value={menu.length} icon="🍽️" />
        <StatCard label="Available"    value={menu.filter((i:any)=>i.available).length}  cls="green" icon="✅" />
        <StatCard label="Unavailable"  value={menu.filter((i:any)=>!i.available).length} cls="red"   icon="❌" />
        <StatCard label="Categories"   value={cats.length} icon="🗂️" />
      </div>

      <div className="card">
        <div className="card-title">
          Menu Items
          <div className="card-actions">
            <input className="search-input" placeholder="🔍 Search..." value={search} onChange={e=>setSearch(e.target.value)} />
            <button className="btn-primary" onClick={openAdd}>+ Add Item</button>
          </div>
        </div>
        <div className="filter-tabs">
          <button className={`filter-tab ${fCat==='all'?'active':''}`} onClick={()=>setFCat('all')}>All ({menu.length})</button>
          {cats.map((c:any) => (
            <button key={c.id} className={`filter-tab ${fCat===c.id?'active':''}`}
              style={fCat===c.id?{borderColor:c.color,color:c.color,background:`${c.color}11`}:{}}
              onClick={()=>setFCat(fCat===c.id?'all':c.id)}>
              {c.icon} {c.name} ({menu.filter((i:any)=>i.catId===c.id).length})
            </button>
          ))}
        </div>
        <table className="data-table">
          <thead><tr><th>Item</th><th>Category</th><th>Price</th><th>Cost</th><th>Margin</th><th>Available</th><th>Actions</th></tr></thead>
          <tbody>
            {visible.map((item:any) => {
              const m   = mgn(item.price, item.cost);
              const cat = cats.find((c:any) => c.id === item.catId);
              return (
                <tr key={item.id}>
                  <td><div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ fontSize:22 }}>{item.emoji}</span><span style={{ fontWeight:600 }}>{item.name}</span></div></td>
                  <td>{cat && <span className="tag" style={{ background:`${cat.color}18`, color:cat.color }}>{cat.icon} {cat.name}</span>}</td>
                  <td className="text-accent" style={{ fontWeight:700 }}>{fmt(item.price)}</td>
                  <td style={{ color:'var(--muted)' }}>{fmt(item.cost)}</td>
                  <td><span className={`tag ${mgnClass(m)}`}>{m}%</span></td>
                  <td><Toggle on={item.available} onChange={()=>toggleMut.mutate(item.id)} /></td>
                  <td>
                    <div style={{ display:'flex', gap:5 }}>
                      <button className="btn-secondary" onClick={()=>openEdit(item)}>✏️</button>
                      <button className="btn-secondary btn-danger" onClick={()=>{ if(window.confirm('Delete item?')) delMut.mutate(item.id); }}>🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visible.length === 0 && <div className="empty-state"><div className="empty-icon">🍽️</div><div>No items found</div></div>}
      </div>

      {modal && (
        <Modal title={sel?`Edit — ${sel.name}`:'Add Menu Item'} onClose={()=>setModal(false)}
          onConfirm={()=>saveMut.mutate({ name:form.name, catId:form.catId, price:parseFloat(form.price)||0, cost:parseFloat(form.cost)||0, emoji:form.emoji, description:form.description })}
          confirmText={sel?'Save Changes':'Add Item'}>
          <div className="form-row cols-2">
            <div className="form-group"><label className="form-label">Name</label><input className="form-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Emoji</label><input className="form-input" value={form.emoji} onChange={e=>setForm(f=>({...f,emoji:e.target.value}))} /></div>
          </div>
          <div className="form-row cols-2">
            <div className="form-group"><label className="form-label">Price (KWD)</label><input className="form-input" type="number" step="0.001" value={form.price} onChange={e=>setForm(f=>({...f,price:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Cost (KWD)</label><input className="form-input" type="number" step="0.001" value={form.cost} onChange={e=>setForm(f=>({...f,cost:e.target.value}))} /></div>
          </div>
          <div className="form-row cols-2">
            <div className="form-group"><label className="form-label">Category</label>
              <select className="form-select" value={form.catId} onChange={e=>setForm(f=>({...f,catId:e.target.value}))}>
                {cats.map((c:any)=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></div>
          </div>
        </Modal>
      )}
    </div>
  );
};
